/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-6b 管理部門向けの確認画面 (`/shift/attendance-review`、D-1028)。
 * バックエンドは実装・レビュー完了済み (apps/api/src/routes/attendance-review.ts)。
 *
 * 人別(1人1行)/店舗別(勤務店舗基準、上位ノードで小計)を Segmented control で切替え、行を開くと
 * 月次明細(読み取り専用)を表示する。「出力へ進む」で現在の期間・店舗フィルタをクエリパラメータに
 * 載せて I-6 (`/shift/attendance-exports`) へ遷移し、遷移先で「実行」を押した際の実行モーダルの
 * 初期値として反映される (§13-39 QA/design-and-code是正)。
 *
 * 設計: docs/design/v04-monthly-attendance-bulk-entry-phase2-detail.md §9-0/§9-1/§13-39
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown, ChevronRight, SlidersHorizontal, AlertTriangle, ArrowRight, Save, Trash2, Info,
} from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore, type NodeTreeItem } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';
import { currentMonthStart } from '@/lib/fiscal-year';
import { isoToLocalHm } from '@/lib/shift-format';
import { computeExportRangeValidity } from './AttendanceExportsPage';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { DateLabel } from '@/components/DateLabel';
import { StatusBadge } from '@/components/WorkRecordModal';
import { MonthPicker } from '@/components/MonthPicker';
import { NodeMultiSelect } from '@/components/NodeMultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  type AttendanceReviewFilters, type AttendanceReviewPersonRow, type AttendanceReviewStoreRow,
  type AttendanceReviewPreset, type AttendanceReviewDetailRecord,
  fetchAttendanceReviewByPerson, fetchAttendanceReviewByStore, fetchAttendanceReviewPersonDetail,
  fetchAttendanceReviewPresets, createAttendanceReviewPreset, updateAttendanceReviewPreset, deleteAttendanceReviewPreset,
} from '@/lib/attendance-review-api';

type EmploymentType = { type_id: string; code: string; label: string };
type ReasonCode = { reason_code_id: string; label: string; category: string; sort_order: number };
type ViewMode = 'person' | 'store';
type EmploymentStatusFilter = 'all' | 'active' | 'terminated';

export type FilterState = {
  startMonth: string;
  endMonth: string;
  nodeIds: Set<string>;
  employmentTypeId: string; // '' = 全て
  employmentStatus: EmploymentStatusFilter;
};

const defaultFilters = (): FilterState => ({
  startMonth: currentMonthStart(), endMonth: currentMonthStart(), nodeIds: new Set(), employmentTypeId: '', employmentStatus: 'all',
});

type SerializedFilters = {
  start_month: string; end_month: string; node_ids: string[]; employment_type_id: string; employment_status: EmploymentStatusFilter;
};

/** test-runner HIGH是正: フィルタの往復シリアライズをコンポーネントのクロージャから抜き出した純粋関数。 */
export const serializeFilters = (f: FilterState): string => JSON.stringify({
  start_month: f.startMonth, end_month: f.endMonth, node_ids: [...f.nodeIds],
  employment_type_id: f.employmentTypeId, employment_status: f.employmentStatus,
} satisfies SerializedFilters);

/** プリセットの filters_json は user 入力由来の任意 JSON のため、型を保証せず1件ずつ検証する。 */
export const deserializeFilters = (json: string | null): Partial<FilterState> | null => {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as Partial<SerializedFilters>;
    const next: Partial<FilterState> = {};
    if (typeof parsed.start_month === 'string') next.startMonth = parsed.start_month;
    if (typeof parsed.end_month === 'string') next.endMonth = parsed.end_month;
    if (Array.isArray(parsed.node_ids)) next.nodeIds = new Set(parsed.node_ids.filter((v): v is string => typeof v === 'string'));
    if (typeof parsed.employment_type_id === 'string') next.employmentTypeId = parsed.employment_type_id;
    if (parsed.employment_status === 'all' || parsed.employment_status === 'active' || parsed.employment_status === 'terminated') {
      next.employmentStatus = parsed.employment_status;
    }
    return next;
  } catch {
    return null;
  }
};

export const toReviewFilters = (f: FilterState): AttendanceReviewFilters => ({
  start_month: f.startMonth, end_month: f.endMonth,
  node_ids: f.nodeIds.size > 0 ? [...f.nodeIds] : undefined,
  employment_type_id: f.employmentTypeId || null,
  employment_status: f.employmentStatus,
});

const hoursOf = (minutes: number): string => (minutes / 60).toFixed(1);

const APPROVAL_STATUS_LABEL: Record<string, string> = {
  no_data: '記録なし', draft: '下書き', submitted: '提出済み', approved: '承認済み', rejected_present: '差戻あり',
};
const UNDETERMINABLE_LABEL: Record<string, string> = {
  no_attendance_settings: '勤怠設定が未登録のため一部の項目は算出できません',
  variable_working_hours: '変形労働時間制のため一部の項目は自動算出できません',
};

/** 767px以下でフィルタ行を折りたたみ式にする (§9-0)。 */
const useIsNarrow = (breakpoint: number): boolean => {
  const [narrow, setNarrow] = useState(() => (typeof window === 'undefined' ? false : window.innerWidth < breakpoint));
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
};

const errorMessageOf = (e: unknown, fallback: string): string => (e instanceof ApiClientError ? e.message : fallback);

// =============================================================================
// フィルタ (共通の中身。 <768px ではシート内、それ以外ではインライン行として使う)
// =============================================================================
function FiltersPanel({ filters, onChange, employmentTypes, scopeRoots }: {
  filters: FilterState; onChange: (next: FilterState) => void;
  employmentTypes: EmploymentType[]; scopeRoots: NodeTreeItem[];
}) {
  const { rangeInvalid, spanTooLong } = computeExportRangeValidity(filters.startMonth, filters.endMonth);
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-xs text-text-sub mb-1">期間開始</label>
          <MonthPicker aria-label="期間開始" value={filters.startMonth} onChange={(v) => onChange({ ...filters, startMonth: `${v}-01` })}
            className="px-3 py-1.5 rounded border border-surface-border text-sm" />
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">期間終了</label>
          <MonthPicker aria-label="期間終了" align="right" value={filters.endMonth} onChange={(v) => onChange({ ...filters, endMonth: `${v}-01` })}
            className="px-3 py-1.5 rounded border border-surface-border text-sm" />
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">店舗 (階層で選択、未選択=閲覧可能な全店舗)</label>
          <NodeMultiSelect nodes={scopeRoots} selectedIds={filters.nodeIds} onChange={(next) => onChange({ ...filters, nodeIds: next })} />
        </div>
        <div className="w-40">
          <label className="block text-xs text-text-sub mb-1">雇用区分</label>
          <Select value={filters.employmentTypeId || '__all__'} onValueChange={(v) => onChange({ ...filters, employmentTypeId: v === '__all__' ? '' : v })}>
            <SelectTrigger aria-label="雇用区分"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">全て</SelectItem>
              {employmentTypes.map((t) => <SelectItem key={t.type_id} value={t.type_id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <label className="block text-xs text-text-sub mb-1">状態</label>
          <Select value={filters.employmentStatus} onValueChange={(v) => onChange({ ...filters, employmentStatus: v as EmploymentStatusFilter })}>
            <SelectTrigger aria-label="状態"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全て</SelectItem>
              <SelectItem value="active">在籍</SelectItem>
              <SelectItem value="terminated">退職済み</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {rangeInvalid && <p className="text-xs text-danger-text">期間終了は期間開始以降にしてください。</p>}
      {!rangeInvalid && spanTooLong && <p className="text-xs text-danger-text">期間か店舗を絞ってください (対象期間は最大3か月です)。</p>}
    </div>
  );
}

// =============================================================================
// 保存プリセット (D-1053: テナント共有・作成者のみ編集可)
// =============================================================================
function PresetsBar({ filters, onApply }: { filters: FilterState; onApply: (next: Partial<FilterState>) => void }) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false;
  const { data } = useQuery({ queryKey: ['attendance-review-presets'], queryFn: fetchAttendanceReviewPresets });
  const presets = data?.presets ?? [];
  const [selectedId, setSelectedId] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AttendanceReviewPreset | null>(null);
  const [label, setLabel] = useState('');

  const selected = presets.find((p) => p.preset_id === selectedId) ?? null;
  const canEditSelected = !!selected && (selected.created_by === user?.user_id || isAdmin);

  const applyPreset = (preset: AttendanceReviewPreset) => {
    setSelectedId(preset.preset_id);
    const parsed = deserializeFilters(preset.filters_json);
    if (parsed) onApply(parsed);
    else toast.error('保存された条件を読み込めませんでした');
  };

  const createMut = useMutation({
    mutationFn: () => createAttendanceReviewPreset({ label: label.trim(), filters_json: serializeFilters(filters) }),
    onSuccess: (res) => {
      toast.success('保存しました');
      setSaveOpen(false); setLabel('');
      qc.invalidateQueries({ queryKey: ['attendance-review-presets'] });
      setSelectedId(res.preset_id);
    },
    onError: (e) => toast.error(errorMessageOf(e, '保存に失敗しました')),
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error('no selection');
      return updateAttendanceReviewPreset(selected.preset_id, { filters_json: serializeFilters(filters), row_version: selected.row_version });
    },
    onSuccess: () => {
      toast.success('現在の条件で更新しました');
      qc.invalidateQueries({ queryKey: ['attendance-review-presets'] });
    },
    onError: (e) => toast.error(errorMessageOf(e, '更新に失敗しました')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteAttendanceReviewPreset(id),
    onSuccess: () => {
      toast.success('削除しました');
      setDeleteTarget(null);
      if (selectedId === deleteTarget?.preset_id) setSelectedId('');
      qc.invalidateQueries({ queryKey: ['attendance-review-presets'] });
    },
    onError: (e) => { toast.error(errorMessageOf(e, '削除に失敗しました')); setDeleteTarget(null); },
  });

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="w-56">
        <Select value={selectedId || '__none__'} onValueChange={(v) => {
          if (v === '__none__') { setSelectedId(''); return; }
          const p = presets.find((x) => x.preset_id === v);
          if (p) applyPreset(p);
        }}>
          <SelectTrigger aria-label="保存済みの絞り込み条件"><SelectValue placeholder="保存済みの条件" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">(保存済みの条件を選ぶ)</SelectItem>
            {presets.map((p) => <SelectItem key={p.preset_id} value={p.preset_id}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button variant="secondary" size="sm" leftIcon={<Save size={14} aria-hidden />} onClick={() => setSaveOpen(true)}>現在の条件を保存</Button>
      {canEditSelected && (
        <>
          <Button variant="secondary" size="sm" loading={updateMut.isPending} onClick={() => updateMut.mutate()}>この条件を更新</Button>
          <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} aria-hidden />} onClick={() => setDeleteTarget(selected)}>削除</Button>
        </>
      )}

      {saveOpen && (
        <Modal open title="現在の条件を保存" onClose={() => setSaveOpen(false)} size="sm"
          footer={(
            <>
              <Button variant="secondary" onClick={() => setSaveOpen(false)}>キャンセル</Button>
              <Button variant="primary" disabled={label.trim().length === 0 || createMut.isPending} loading={createMut.isPending} onClick={() => createMut.mutate()}>保存</Button>
            </>
          )}>
          <div>
            <label className="block text-sm text-text-sub mb-1">名称</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100}
              className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" placeholder="例: 全店舗・直近3か月" />
          </div>
          <p className="text-xs text-text-sub mt-2">保存した条件はテナント内の閲覧権限を持つ人全員が使えます。編集・削除は作成者のみ行えます。</p>
        </Modal>
      )}
      <ConfirmDialog open={!!deleteTarget} title="保存済みの条件を削除しますか" destructive
        message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.preset_id)}
        onClose={() => setDeleteTarget(null)} />
    </div>
  );
}

// =============================================================================
// 人別テーブル
// =============================================================================
const numCls = 'tabular-nums text-right whitespace-nowrap px-2 py-1.5';
const thCls = 'text-xs font-semibold text-text-sub text-right whitespace-nowrap px-2 py-1.5';

function PersonWarning({ row }: { row: AttendanceReviewPersonRow }) {
  if (!row.determinable) {
    return <span title={UNDETERMINABLE_LABEL[row.reason ?? ''] ?? '一部の項目は算出できません'}><AlertTriangle size={14} className="text-alert-text" aria-hidden /></span>;
  }
  if (row.has_minor_labor_violation) {
    return <span title="18歳未満の労働基準に抵触する可能性のある勤務があります"><AlertTriangle size={14} className="text-danger-text" aria-hidden /></span>;
  }
  return null;
}

function PersonTableRows({ rows, onOpen, employmentTypeLabelById }: {
  rows: AttendanceReviewPersonRow[]; onOpen: (row: AttendanceReviewPersonRow) => void; employmentTypeLabelById: Map<string, string>;
}) {
  if (rows.length === 0) {
    return <tr><td colSpan={16} className="py-8"><EmptyState message="対象者がいません" /></td></tr>;
  }
  return (
    <>
      {rows.map((row) => (
        <tr key={row.person_key} tabIndex={0} role="button" className="border-t border-surface-border hover:bg-surface-sunken cursor-pointer focus-ring"
          onClick={() => onOpen(row)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(row); } }}>
          <td className="sticky left-0 bg-white px-2 py-1.5 text-sm font-medium text-text-body whitespace-nowrap">
            <span className="inline-flex items-center gap-1">{row.name ?? '(氏名不明)'}<PersonWarning row={row} /></span>
          </td>
          <td className="px-2 py-1.5 text-xs text-text-sub whitespace-nowrap">
            {row.home_node_out_of_scope ? '他店舗' : (row.home_node_name ?? '—')}
          </td>
          <td className="px-2 py-1.5 text-xs text-text-sub whitespace-nowrap">
            {row.employment_type_id ? (employmentTypeLabelById.get(row.employment_type_id) ?? row.employment_type_id) : '—'}
          </td>
          <td className={numCls}>{row.worked_days}</td>
          <td className={numCls}>{hoursOf(row.total_labor_minutes)}</td>
          <td className={numCls}>
            {row.scheduled_monthly_minutes_contracted != null ? hoursOf(row.scheduled_monthly_minutes_contracted) : '—'}
          </td>
          <td className={numCls}>{hoursOf(row.non_statutory_overtime_minutes + row.statutory_overtime_minutes)}</td>
          <td className={numCls}>{hoursOf(row.night_minutes)}</td>
          <td className={numCls}>{row.late_count}</td>
          <td className={numCls}>{row.early_leave_count}</td>
          <td className={numCls}>{row.public_holiday_days}</td>
          <td className={numCls}>{row.paid_leave_days}</td>
          <td className={numCls}>{row.compensatory_off_days}</td>
          <td className={numCls}>{row.absence_days}</td>
          <td className={numCls}>{row.holiday_work_days}</td>
          <td className="px-2 py-1.5 text-xs text-text-sub whitespace-nowrap">{APPROVAL_STATUS_LABEL[row.approval_status ?? ''] ?? row.approval_status}</td>
        </tr>
      ))}
    </>
  );
}

function PersonTableHeader() {
  return (
    <tr>
      <th className="sticky left-0 bg-white text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">氏名</th>
      <th className="text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">所属</th>
      <th className="text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">雇用区分</th>
      <th className={thCls}>出勤日数</th>
      <th className={thCls}>総労働(h)</th>
      <th className={thCls}>
        <span className="inline-flex items-center gap-0.5" title="週の契約労働時間(所定労働時間)を対象期間の日数ぶんに換算した参考値。契約未設定の社員は「—」(既定8時間/日で残業を計算)。">
          所定(h)<Info size={11} className="text-text-sub shrink-0" aria-hidden />
        </span>
      </th>
      <th className={thCls}>
        <span className="inline-flex items-center gap-0.5" title="残業(h) = 実労働時間 − 「その日の所定労働時間(シフト予定があればその時間、無ければ週契約から換算した1日あたりの時間)」と「法定労働時間(1日8時間/週40時間)」のうち大きい方、を超えた時間の合計。">
          残業(h)<Info size={11} className="text-text-sub shrink-0" aria-hidden />
        </span>
      </th>
      <th className={thCls}>
        <span className="inline-flex items-center gap-0.5" title="深夜(h) = 実際に働いた時間帯のうち、店舗の深夜時間帯設定(例: 22:00〜翌5:00)と重なる時間の合計。残業の判定とは独立した計算のため、同じ時間が残業と深夜の両方にカウントされることがある。">
          深夜(h)<Info size={11} className="text-text-sub shrink-0" aria-hidden />
        </span>
      </th>
      <th className={thCls}>遅刻</th>
      <th className={thCls}>早退</th>
      <th className={thCls}>公休</th>
      <th className={thCls}>有休</th>
      <th className={thCls}>代休</th>
      <th className={thCls}>欠勤</th>
      <th className={thCls}>休出</th>
      <th className="text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">状況</th>
    </tr>
  );
}

function PersonView({ filters, onOpenPerson, employmentTypeLabelById }: {
  filters: FilterState; onOpenPerson: (row: AttendanceReviewPersonRow) => void; employmentTypeLabelById: Map<string, string>;
}) {
  const { valid } = computeExportRangeValidity(filters.startMonth, filters.endMonth);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['attendance-review-by-person', toReviewFilters(filters)],
    queryFn: () => fetchAttendanceReviewByPerson(toReviewFilters(filters)),
    enabled: valid,
  });
  if (!valid) return null;
  if (isLoading) return <p className="text-sm text-text-sub py-4">読み込み中…</p>;
  if (isError) return <p className="text-sm text-danger-text py-4">{errorMessageOf(error, '取得に失敗しました')}</p>;
  return (
    <div className="overflow-x-auto border border-surface-border rounded-lg">
      <table className="min-w-max w-full">
        <thead><PersonTableHeader /></thead>
        <tbody><PersonTableRows rows={data?.persons ?? []} onOpen={onOpenPerson} employmentTypeLabelById={employmentTypeLabelById} /></tbody>
      </table>
    </div>
  );
}

// =============================================================================
// 店舗別テーブル (§9-0: 勤務店舗基準、2店舗勤務者は各店舗に計上、人数は重複除外)
// =============================================================================
export type ParentGroup = { parentNodeId: string | null; parentNodeName: string | null; stores: AttendanceReviewStoreRow[] };

export const groupStoresByParent = (stores: AttendanceReviewStoreRow[]): ParentGroup[] => {
  const order: string[] = [];
  const map = new Map<string, ParentGroup>();
  for (const s of stores) {
    const key = s.parent_node_id ?? '__root__';
    if (!map.has(key)) { map.set(key, { parentNodeId: s.parent_node_id, parentNodeName: s.parent_node_name, stores: [] }); order.push(key); }
    map.get(key)!.stores.push(s);
  }
  return order.map((k) => map.get(k)!);
};

/** test-runner HIGH是正: 上位ノード小計の人数 (2店舗以上勤務者の重複除外) をコンポーネントの
 *  JSX内reduceから抜き出した純粋関数。 */
export const computeGroupPersonCount = (group: ParentGroup): number =>
  new Set(group.stores.flatMap((s) => s.persons.map((p) => p.person_key))).size;

function StoreView({ filters, onOpenPerson }: { filters: FilterState; onOpenPerson: (row: AttendanceReviewPersonRow) => void }) {
  const { valid } = computeExportRangeValidity(filters.startMonth, filters.endMonth);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['attendance-review-by-store', toReviewFilters(filters)],
    queryFn: () => fetchAttendanceReviewByStore(toReviewFilters(filters)),
    enabled: valid,
  });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [expandedStores, setExpandedStores] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) => setCollapsedGroups((cur) => {
    const next = new Set(cur); if (next.has(key)) next.delete(key); else next.add(key); return next;
  });
  const toggleStore = (nodeId: string) => setExpandedStores((cur) => {
    const next = new Set(cur); if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId); return next;
  });

  if (!valid) return null;
  if (isLoading) return <p className="text-sm text-text-sub py-4">読み込み中…</p>;
  if (isError) return <p className="text-sm text-danger-text py-4">{errorMessageOf(error, '取得に失敗しました')}</p>;

  const stores = data?.stores ?? [];
  if (stores.length === 0) return <EmptyState message="対象店舗がありません" />;
  const groups = groupStoresByParent(stores);

  return (
    <div className="space-y-2">
      <p className="text-xs text-text-sub">
        店舗の合計は人別の合計と一致しません (2店舗以上で勤務した人はそれぞれの店舗に計上されるため)。
        全体の合計人数 ({data?.total_person_count ?? 0}名) は重複を除いた人数です。
      </p>
      <div className="overflow-x-auto border border-surface-border rounded-lg">
        <table className="min-w-max w-full">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">店舗 / 上位ノード</th>
              <th className={thCls}>人数</th>
              <th className={thCls}>出勤日数計</th>
              <th className={thCls}>総労働計(h)</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const groupKey = group.parentNodeId ?? '__root__';
              const collapsed = collapsedGroups.has(groupKey);
              const groupPersonCount = computeGroupPersonCount(group);
              const workedDaysSum = group.stores.reduce((sum, s) => sum + s.worked_days_total, 0);
              const laborMinutesSum = group.stores.reduce((sum, s) => sum + s.total_labor_minutes_total, 0);
              return (
                <Fragment key={groupKey}>
                  <tr tabIndex={0} role="button" aria-expanded={!collapsed}
                    className="border-t border-surface-border bg-surface-sunken cursor-pointer focus-ring"
                    onClick={() => toggleGroup(groupKey)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(groupKey); } }}>
                    <td className="sticky left-0 bg-surface-sunken px-2 py-1.5 text-sm font-bold text-text-body whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {collapsed ? <ChevronRight size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
                        {group.parentNodeName ?? '(上位ノードなし)'} 小計
                      </span>
                    </td>
                    <td className={`${numCls} font-bold`}>{groupPersonCount}</td>
                    <td className={`${numCls} font-bold`}>{workedDaysSum}</td>
                    <td className={`${numCls} font-bold`}>{hoursOf(laborMinutesSum)}</td>
                  </tr>
                  {!collapsed && group.stores.map((store) => (
                    <Fragment key={store.node_id}>
                      <tr tabIndex={0} role="button" aria-expanded={expandedStores.has(store.node_id)}
                        className="border-t border-surface-border hover:bg-surface-sunken cursor-pointer focus-ring"
                        onClick={() => toggleStore(store.node_id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleStore(store.node_id); } }}>
                        <td className="sticky left-0 bg-white pl-6 px-2 py-1.5 text-sm text-text-body whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            {expandedStores.has(store.node_id) ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
                            {store.node_name}
                          </span>
                        </td>
                        <td className={numCls}>{store.person_count}</td>
                        <td className={numCls}>{store.worked_days_total}</td>
                        <td className={numCls}>{hoursOf(store.total_labor_minutes_total)}</td>
                      </tr>
                      {expandedStores.has(store.node_id) && store.persons.map((p) => (
                        <tr key={p.person_key} tabIndex={0} role="button"
                          className="border-t border-surface-border hover:bg-surface-sunken cursor-pointer focus-ring"
                          onClick={(e) => { e.stopPropagation(); onOpenPerson(p); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenPerson(p); } }}>
                          <td className="sticky left-0 bg-white pl-12 px-2 py-1.5 text-xs text-text-sub whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">{p.name ?? '(氏名不明)'}<PersonWarning row={p} /></span>
                          </td>
                          <td className={numCls}>—</td>
                          <td className={numCls}>{p.worked_days}</td>
                          <td className={numCls}>{hoursOf(p.total_labor_minutes)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// 月次明細 (読み取り専用)
// =============================================================================
function PersonDetailPanel({ row, period, onClose, isNarrow }: {
  row: AttendanceReviewPersonRow; period: { start_month: string; end_month: string }; onClose: () => void; isNarrow: boolean;
}) {
  // GET /nodes はテナント全体の active ノードを返す (routes/nodes.ts、閲覧権限スコープに依らない)ため、
  // グローバルに populate 済みの useScopeStore をそのまま使って店舗名を解決してよい
  // (応援勤務先の店舗名が閲覧スコープ外でも解決できる、MonthlyAttendanceEntryPage.tsx と同じ前提)。
  const scopeRoots = useScopeStore((s) => s.roots);
  const nodeNameById = useMemo(() => new Map(flattenNodes(scopeRoots).map(({ node }) => [node.node_id, node.name])), [scopeRoots]);
  const { data: reasonCodesData } = useQuery({
    queryKey: ['attendance-reason-codes', 'attendance-review'],
    queryFn: () => apiFetch<{ reason_codes: ReasonCode[] }>('/masters/attendance-reason-codes'),
  });
  const reasonById = useMemo(() => new Map((reasonCodesData?.reason_codes ?? []).map((r) => [r.reason_code_id, r.label])), [reasonCodesData]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['attendance-review-person-detail', row.person_key, period],
    queryFn: () => fetchAttendanceReviewPersonDetail(row.person_key, period),
  });

  const recordLabel = (r: AttendanceReviewDetailRecord): string => {
    if (r.record_kind === 'absence') return r.reason_code_id ? (reasonById.get(r.reason_code_id) ?? '休み') : '休み';
    return r.reason_code_id ? (reasonById.get(r.reason_code_id) ?? '勤務') : '勤務';
  };

  return (
    <Modal open title={`月次明細: ${row.name ?? '(氏名不明)'}`} onClose={onClose} position={isNarrow ? 'bottom-sheet' : 'right'} size="lg">
      {isLoading ? (
        <p className="text-sm text-text-sub py-4">読み込み中…</p>
      ) : isError ? (
        <p className="text-sm text-danger-text py-4">{errorMessageOf(error, '取得に失敗しました')}</p>
      ) : (data?.work_records.length ?? 0) === 0 ? (
        <EmptyState message="この期間の勤務実績がありません" />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-max w-full text-sm">
            <thead>
              <tr>
                <th className="text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">日付</th>
                <th className="text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">店舗</th>
                <th className="text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">区分</th>
                <th className={thCls}>開始</th>
                <th className={thCls}>終了</th>
                <th className={thCls}>休憩(分)</th>
                <th className={thCls}>実労働(h)</th>
                <th className="text-xs font-semibold text-text-sub text-left px-2 py-1.5 whitespace-nowrap">状態</th>
              </tr>
            </thead>
            <tbody>
              {(data?.work_records ?? []).map((r) => (
                <tr key={r.work_record_id} className="border-t border-surface-border">
                  <td className="px-2 py-1.5 whitespace-nowrap"><DateLabel date={r.work_date} withWeekday /></td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs text-text-sub">{nodeNameById.get(r.work_node_id) ?? r.work_node_id}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs text-text-sub">{recordLabel(r)}</td>
                  <td className={numCls}>{r.start_at ? isoToLocalHm(r.start_at) : '—'}</td>
                  <td className={numCls}>{r.end_at ? isoToLocalHm(r.end_at) : '—'}</td>
                  <td className={numCls}>{r.break_minutes}</td>
                  <td className={numCls}>{r.actual_labor_minutes !== null ? hoursOf(r.actual_labor_minutes) : '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// =============================================================================
// フィルタ折りたたみシート (<768px)
// =============================================================================
function FiltersSheet({ filters, onChange, employmentTypes, scopeRoots, onClose }: {
  filters: FilterState; onChange: (next: FilterState) => void; employmentTypes: EmploymentType[];
  scopeRoots: NodeTreeItem[]; onClose: () => void;
}) {
  return (
    <Modal open title="絞り込み" onClose={onClose} position="bottom-sheet"
      footer={<Button variant="primary" onClick={onClose}>閉じる</Button>}>
      <FiltersPanel filters={filters} onChange={onChange} employmentTypes={employmentTypes} scopeRoots={scopeRoots} />
    </Modal>
  );
}

// =============================================================================
// メイン画面
// =============================================================================
export function AttendanceReviewPage() {
  const navigate = useNavigate();
  const scopeRoots = useScopeStore((s) => s.roots);
  const isNarrow = useIsNarrow(768);

  const [filters, setFilters] = useState<FilterState>(() => defaultFilters());
  const [viewMode, setViewMode] = useState<ViewMode>('person');
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [openPerson, setOpenPerson] = useState<AttendanceReviewPersonRow | null>(null);

  const { data: employmentTypesData } = useQuery({
    queryKey: ['employment-types'], queryFn: () => apiFetch<EmploymentType[]>('/employment-types'),
  });
  const employmentTypes = employmentTypesData ?? [];
  const employmentTypeLabelById = useMemo(() => new Map(employmentTypes.map((t) => [t.type_id, t.label])), [employmentTypes]);

  const applyPartialFilters = (next: Partial<FilterState>) => setFilters((cur) => ({ ...cur, ...next }));

  const goToExports = () => {
    const params = new URLSearchParams();
    params.set('start_month', filters.startMonth);
    params.set('end_month', filters.endMonth);
    if (filters.nodeIds.size > 0) params.set('node_ids', [...filters.nodeIds].join(','));
    navigate(`/shift/attendance-exports?${params.toString()}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-h2">勤怠実績の確認</h2>
          <p className="text-sm text-text-sub mt-1">提出・承認済みの勤務実績を人別・店舗別に確認します。</p>
        </div>
        <Button variant="secondary" leftIcon={<ArrowRight size={14} aria-hidden />} onClick={goToExports}>出力へ進む</Button>
      </div>

      <PresetsBar filters={filters} onApply={applyPartialFilters} />

      {isNarrow ? (
        <Button variant="secondary" size="sm" leftIcon={<SlidersHorizontal size={14} aria-hidden />} onClick={() => setFiltersSheetOpen(true)}>絞り込み</Button>
      ) : (
        <div className="border border-surface-border rounded-lg bg-white p-3">
          <FiltersPanel filters={filters} onChange={setFilters} employmentTypes={employmentTypes} scopeRoots={scopeRoots} />
        </div>
      )}
      {filtersSheetOpen && (
        <FiltersSheet filters={filters} onChange={setFilters} employmentTypes={employmentTypes} scopeRoots={scopeRoots} onClose={() => setFiltersSheetOpen(false)} />
      )}

      <div className="flex rounded border border-surface-border overflow-hidden text-sm w-fit" role="tablist" aria-label="表示切替">
        <button type="button" role="tab" aria-selected={viewMode === 'person'} onClick={() => setViewMode('person')}
          className={`px-3 py-2 min-h-[44px] sm:min-h-0 ${viewMode === 'person' ? 'bg-brand-red text-white' : 'hover:bg-surface-sunken'}`}>人別</button>
        <button type="button" role="tab" aria-selected={viewMode === 'store'} onClick={() => setViewMode('store')}
          className={`px-3 py-2 min-h-[44px] sm:min-h-0 border-l border-surface-border ${viewMode === 'store' ? 'bg-brand-red text-white' : 'hover:bg-surface-sunken'}`}>店舗別</button>
      </div>

      {viewMode === 'person' ? (
        <PersonView filters={filters} onOpenPerson={setOpenPerson} employmentTypeLabelById={employmentTypeLabelById} />
      ) : (
        <StoreView filters={filters} onOpenPerson={setOpenPerson} />
      )}

      {openPerson && (
        <PersonDetailPanel row={openPerson} period={{ start_month: filters.startMonth, end_month: filters.endMonth }}
          onClose={() => setOpenPerson(null)} isNarrow={isNarrow} />
      )}
    </div>
  );
}
